import React from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import useMyGroupBaskets, { groupBasketsAtVendor } from "../../hooks/useMyGroupBaskets";
import { groupBasketPath } from "../../utils/groupBasketLink";
import GroupBasketCountdown from "./GroupBasketCountdown";
import { FONT, GREEN, GREEN_SOFT, LIME, MUTED, PrimaryButton, TEXT } from "./groupBasketStyles";

/*
 * "Your group order at <restaurant>", with the server's deadline counting
 * down and Open: a way back into a basket under way. On a storefront it lists
 * only that restaurant's baskets (`vendorId`); without one, every active
 * basket (the order history). Renders nothing for a signed-out visitor or
 * when there is nothing to resume.
 */
const GroupBasketResumeCards = ({ vendorId = null, className }) => {
  const history = useHistory();
  const { baskets, serverNow, receivedAt } = useMyGroupBaskets();
  const shown = vendorId ? groupBasketsAtVendor(baskets, vendorId) : baskets;
  if (!shown.length) return null;

  return (
    <List className={className} aria-label="Your group orders">
      {shown.map((basket) => (
        <Resume key={basket.id}>
          <span className="icon" aria-hidden="true">
            <GroupsOutlinedIcon />
          </span>
          <span className="text">
            <strong>
              Your group order at {basket.vendorName || "the restaurant"}
            </strong>
            {basket.activeDeadline?.at ? (
              <GroupBasketCountdown
                deadline={basket.activeDeadline}
                serverNow={serverNow}
                receivedAt={receivedAt}
                tone="wait"
              />
            ) : (
              <span className="meta">{basket.isHost ? "You are the host" : `Hosted by ${basket.hostLabel}`}</span>
            )}
          </span>
          <PrimaryButton
            type="button"
            onClick={() => history.push(groupBasketPath(basket.id))}
            aria-label={`Open your group order at ${basket.vendorName || "the restaurant"}`}
          >
            Open
          </PrimaryButton>
        </Resume>
      ))}
    </List>
  );
};

const List = styled.ul`
  list-style: none;
  margin: 12px auto 0;
  padding: 0;
  max-width: 1440px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-family: ${FONT};
`;

const Resume = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 16px;
  background: #ffffff;
  border: 1.5px solid ${LIME};
  box-shadow: 0 8px 22px rgba(21, 35, 42, 0.08);

  .icon {
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: ${GREEN_SOFT};
    color: ${GREEN};
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  strong {
    font-size: 15px;
    font-weight: 800;
    line-height: 1.3;
    color: ${TEXT};
    overflow-wrap: anywhere;
  }

  .meta {
    font-size: 13px;
    color: ${MUTED};
  }

  button {
    flex: 0 0 auto;
    min-width: 88px;
  }
`;

export default GroupBasketResumeCards;
